import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import type { OccasionImageAssetType, OccasionTreeItemType } from '../../schemas/styling';
import type { UploadAssetFile } from './types';

export const toUploadFile = (
  image?: OccasionImageAssetType | null,
  prefix = 'occasion-image',
): Array<UploadAssetFile> => {
  if (!image?.url) {
    return [];
  }

  return [
    {
      uid: `${prefix}-${image.id ?? 'new'}`,
      id: image.id,
      name: image.name || prefix,
      url: image.url,
      status: 'done',
    },
  ];
};

export const toOccasionImage = (fileList: Array<UploadAssetFile>): OccasionImageAssetType | null => {
  const file = fileList[0];
  if (!file) {
    return null;
  }

  const response = file.response ?? {};
  const id = typeof file.id === 'number' ? file.id : response.id;
  const url = file.url || response.url;

  if (!url && typeof id !== 'number') {
    return null;
  }

  return {
    id,
    url,
    name: response.name || file.name,
    usage: response.usage,
    type: response.type,
    uploaded: true,
  };
};

export const sortOccasionTree = (nodes: Array<OccasionTreeItemType>): Array<OccasionTreeItemType> =>
  [...nodes]
    .sort((first, second) => {
      if (first.display_order !== second.display_order) {
        return first.display_order - second.display_order;
      }
      return first.name.localeCompare(second.name);
    })
    .map(node => ({
      ...node,
      children: node.children ? sortOccasionTree(node.children) : [],
    }));

export const filterRootOccasions = (
  roots: Array<OccasionTreeItemType>,
  normalizedSearchTerm: string,
) => {
  if (!normalizedSearchTerm) {
    return roots;
  }

  return roots.filter(root => {
    if (root.name.toLowerCase().includes(normalizedSearchTerm)) {
      return true;
    }

    return (root.children ?? []).some(child =>
      child.name.toLowerCase().includes(normalizedSearchTerm),
    );
  });
};

export const getOptimizedSquareImage = (url?: string, size = 720) =>
  updateAttributesOfCdnImage(url, {
    width: size,
    height: size,
    quality: 100,
  }) || url;

export const getReorderAnchorIds = (
  items: Array<{ id: number }>,
  nextIndex: number,
): { beforeId?: number; afterId?: number } => {
  const beforeId = nextIndex > 0 ? items[nextIndex - 1]?.id : undefined;
  const afterId = nextIndex < items.length - 1 ? items[nextIndex + 1]?.id : undefined;

  return { beforeId, afterId };
};
